export const SALES_MENU_SCOPE_OPTIONS = [
  { value: 'restaurant', label: 'Im Restaurant' },
  { value: 'delivery_partner', label: 'Lieferung Partner' },
  { value: 'delivery_internal', label: 'Lieferung Intern' },
  { value: 'pickup', label: 'Abholung' },
];

export const SALES_MENU_SCOPE_LABELS = Object.fromEntries(
  SALES_MENU_SCOPE_OPTIONS.map((option) => [option.value, option.label]),
);

export const SALES_MENU_CHANNEL_OPTIONS = [
  { value: 'pos_terminal', label: 'Kassa / Bonieren' },
  { value: 'webshop', label: 'Webshop' },
  { value: 'bestellmonitor', label: 'Bestellmonitor' },
  { value: 'foodora', label: 'Foodora' },
  { value: 'lieferando', label: 'Lieferando' },
  { value: 'wolt', label: 'Wolt' },
];

export const SALES_MENU_CHANNEL_LABELS = Object.fromEntries(
  SALES_MENU_CHANNEL_OPTIONS.map((option) => [option.value, option.label]),
);

const MENU_SCOPE_ALIASES = {
  restaurant: 'restaurant',
  'in-restaurant': 'restaurant',
  in_restaurant: 'restaurant',
  'dine-in': 'restaurant',
  dine_in: 'restaurant',
  delivery_partner: 'delivery_partner',
  'delivery-partner': 'delivery_partner',
  online_partner: 'delivery_partner',
  delivery_internal: 'delivery_internal',
  'delivery-internal': 'delivery_internal',
  internal_delivery: 'delivery_internal',
  pickup: 'pickup',
  takeaway: 'pickup',
  'take-away': 'pickup',
};

const MENU_CHANNEL_ALIASES = {
  pos_terminal: 'pos_terminal',
  pos: 'pos_terminal',
  terminal: 'pos_terminal',
  kassa: 'pos_terminal',
  bonieren: 'pos_terminal',
  webshop: 'webshop',
  website: 'webshop',
  online_shop: 'webshop',
  bestellmonitor: 'bestellmonitor',
  monitor: 'bestellmonitor',
  kitchen_monitor: 'bestellmonitor',
  foodora: 'foodora',
  lieferando: 'lieferando',
  wolt: 'wolt',
};

const LEGACY_MENU_TYPES = {
  restaurant: 'dine-in',
  delivery_partner: 'delivery',
  delivery_internal: 'delivery',
  pickup: 'pickup',
};

const DEFAULT_CHANNEL_VISIBILITY_BY_SCOPE = {
  restaurant: {
    pos_terminal: true,
    webshop: true,
    bestellmonitor: true,
    foodora: false,
    lieferando: false,
    wolt: false,
  },
  delivery_partner: {
    pos_terminal: false,
    webshop: false,
    bestellmonitor: true,
    foodora: true,
    lieferando: true,
    wolt: true,
  },
  delivery_internal: {
    pos_terminal: true,
    webshop: true,
    bestellmonitor: true,
    foodora: false,
    lieferando: false,
    wolt: false,
  },
  pickup: {
    pos_terminal: true,
    webshop: true,
    bestellmonitor: true,
    foodora: false,
    lieferando: false,
    wolt: false,
  },
};

export function createCatalogId(prefix = 'id') {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildEmptySalesCatalog() {
  return { menus: [] };
}

export function normalizeMenuScope(scope) {
  return MENU_SCOPE_ALIASES[String(scope || '').trim().toLowerCase()] || 'restaurant';
}

export function normalizeMenuChannel(channel) {
  return MENU_CHANNEL_ALIASES[String(channel || '').trim().toLowerCase()] || '';
}

export function buildDefaultMenuVisibility(scope) {
  const safeScope = normalizeMenuScope(scope);
  return {
    ...(DEFAULT_CHANNEL_VISIBILITY_BY_SCOPE[safeScope] || DEFAULT_CHANNEL_VISIBILITY_BY_SCOPE.restaurant),
  };
}

export function normalizeMenuVisibility(scope, visibility) {
  const next = buildDefaultMenuVisibility(scope);
  if (!visibility || typeof visibility !== 'object' || Array.isArray(visibility)) {
    return next;
  }

  Object.entries(visibility).forEach(([rawChannel, rawValue]) => {
    const channel = normalizeMenuChannel(rawChannel);
    if (!channel) return;
    next[channel] = rawValue !== false;
  });

  return next;
}

export function isMenuVisibleInChannel(menu, channel = 'pos_terminal') {
  const safeChannel = normalizeMenuChannel(channel);
  if (!safeChannel) return true;
  const visibility = normalizeMenuVisibility(menu?.scope, menu?.visibility);
  return visibility[safeChannel] !== false;
}

export function normalizeSalesCatalog(catalog) {
  const rawMenus = sortBySortOrder(Array.isArray(catalog?.menus) ? catalog.menus : []);
  return {
    menus: rawMenus.map((menu, menuIndex) => normalizeMenu(menu, menuIndex)),
  };
}

export function deriveLegacyMenusFromSalesCatalog(catalog) {
  const normalized = normalizeSalesCatalog(catalog);
  return {
    activeMenus: normalized.menus.map((menu) => ({
      id: menu.id,
      name: menu.name,
      type: LEGACY_MENU_TYPES[menu.scope] || 'all',
      visible: menu.enabled !== false && isMenuVisibleInChannel(menu, 'webshop'),
      categoryIds: [],
    })),
    featuredCategoryIds: [],
    featuredProductIds: [],
  };
}

export function scopeFromFulfillment(fulfillmentType, orderSource = 'pos') {
  const safe = String(fulfillmentType || '').trim().toLowerCase();
  if (safe === 'delivery') {
    return orderSource === 'partner' ? 'delivery_partner' : 'delivery_internal';
  }
  if (safe === 'pickup' || safe === 'takeaway') {
    return 'pickup';
  }
  return 'restaurant';
}

export function getMenusForScope(catalog, scope, channel = 'pos_terminal') {
  const normalized = normalizeSalesCatalog(catalog);
  const safeScope = normalizeMenuScope(scope);
  return normalized.menus.filter(
    (menu) =>
      menu.enabled !== false &&
      normalizeMenuScope(menu.scope) === safeScope &&
      isMenuVisibleInChannel(menu, channel),
  );
}

export function buildCatalogViewFromMenu(menu, products) {
  if (!menu) {
    return {
      categories: [],
      products: Array.isArray(products) ? products : [],
    };
  }

  const productsById = new Map((products || []).map((product) => [product.id, product]));
  const nextProducts = [];
  const nextCategories = [];

  (menu.groups || []).forEach((group, groupIndex) => {
    if (group.enabled === false) return;
    const groupProducts = [];

    (group.items || []).forEach((item, itemIndex) => {
      if (item.enabled === false || !item.productId) return;
      const baseProduct = productsById.get(item.productId);
      if (!baseProduct) return;

      groupProducts.push({
        ...baseProduct,
        name: item.label?.trim() || baseProduct.name,
        description: item.description?.trim() || baseProduct.description || '',
        price: Number.isFinite(Number(item.price)) ? Number(item.price) : Number(baseProduct.price || 0),
        category_id: group.id,
        __menu_id: menu.id,
        __menu_item_id: item.id,
        __menu_group_id: group.id,
        __menu_key: item.id || `${group.id}-${baseProduct.id}-${itemIndex}`,
      });
    });

    if (groupProducts.length === 0) return;

    nextCategories.push({
      id: group.id,
      name: group.name,
      color: group.color || '#18323b',
      icon: 'Package',
      sort_order: Number.isFinite(Number(group.sort_order)) ? Number(group.sort_order) : groupIndex,
    });
    nextProducts.push(...groupProducts);
  });

  return {
    categories: nextCategories,
    products: nextProducts,
  };
}

function normalizeMenu(menu, menuIndex) {
  const scope = normalizeMenuScope(menu?.scope);

  return {
    id: menu?.id || createCatalogId('menu'),
    name: String(menu?.name || `Menu ${menuIndex + 1}`),
    scope,
    enabled: menu?.enabled !== false,
    visibility: normalizeMenuVisibility(
      scope,
      menu?.visibility || menu?.visible_in || menu?.channels,
    ),
    sort_order: Number.isFinite(Number(menu?.sort_order)) ? Number(menu.sort_order) : menuIndex,
    groups: sortBySortOrder(Array.isArray(menu?.groups) ? menu.groups : []).map((group, groupIndex) =>
      normalizeGroup(group, groupIndex),
    ),
  };
}

function normalizeGroup(group, groupIndex) {
  return {
    id: group?.id || createCatalogId('group'),
    name: String(group?.name || `Gruppe ${groupIndex + 1}`),
    color: String(group?.color || ''),
    enabled: group?.enabled !== false,
    sort_order: Number.isFinite(Number(group?.sort_order)) ? Number(group.sort_order) : groupIndex,
    items: sortBySortOrder(Array.isArray(group?.items) ? group.items : []).map((item, itemIndex) =>
      normalizeItem(item, itemIndex),
    ),
  };
}

function normalizeItem(item, itemIndex) {
  return {
    id: item?.id || createCatalogId('item'),
    productId: item?.productId || item?.product_id || '',
    label: String(item?.label || ''),
    description: String(item?.description || ''),
    price: Number.isFinite(Number(item?.price)) ? Number(item.price) : 0,
    enabled: item?.enabled !== false,
    sort_order: Number.isFinite(Number(item?.sort_order)) ? Number(item.sort_order) : itemIndex,
  };
}

function sortBySortOrder(entries) {
  return [...entries].sort((left, right) => {
    const leftOrder = Number.isFinite(Number(left?.sort_order)) ? Number(left.sort_order) : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(Number(right?.sort_order)) ? Number(right.sort_order) : Number.MAX_SAFE_INTEGER;

    if (leftOrder === rightOrder) return 0;
    return leftOrder - rightOrder;
  });
}
